<template>
    <v-container fluid>
        <v-layout wrap justify-center>
            <!-- emerald logo -->
            <v-flex xs12 class="d-flex flex-column justify-center align-center mb-4" >
                <v-img width="160" contain src="../../assets/Emerald.png" />
                <v-slide-x-transition>
                    <div v-if="!loading" class="d-flex flex-wrap justify-center align-center mt-2">
                        <v-radio-group v-model="showOption" dense row hide-details class="ma-0">
                            <!-- active -->
                            <v-radio :value="0" :label="$t('QuickFinancialFileSelector.active')"></v-radio>

                            <!-- locked -->
                            <v-radio :value="1" :label="$t('QuickFinancialFileSelector.locked')"></v-radio>

                            <!-- revolved -->
                            <v-radio :value="2" :label="$t('QuickFinancialFileSelector.revolved')"></v-radio>

                            <!-- archived -->
                            <v-radio :value="3" :label="$t('QuickFinancialFileSelector.archived')"></v-radio>
                        </v-radio-group>
                   </div>
                </v-slide-x-transition>
            </v-flex>

            <!-- loader -->
            <v-flex sm2 v-if="loading" class="loader">
                <v-progress-linear indeterminate class="rounded-t-xl"/>
                <v-progress-linear indeterminate reverse color="primary lighten-2" class="rounded-b-xl"/>
            </v-flex>

            <!-- financial files cards -->
            <v-layout
            	v-else
                wrap
                justify-center
                class="py-3 overflow-y-auto"
            	:style="{ maxHeight: $vuetify.breakpoint.height - 280 + 'px' }"
            >
                <v-flex
                    v-for="(financialFile, index) in filteredFinancialFiles"
                    :key="financialFile.id"
                    :lg1="filteredFinancialFiles.length > 15"
                    :lg2="filteredFinancialFiles.length < 15"
                    md2
                    sm3
                    xs6
                    class="financial-file-card mx-1 mb-2 rounded-lg"
                    :style="{animationDelay: `${index*2.4/20}s`}"
                >
                    <v-card
                        flat
                        width="100%"
                        class="financial-file-card__body rounded-lg overflow-hidden"
                        :loading="selectedFinancialFileId === financialFile.id"
                        :disabled="selectedFinancialFileId && selectedFinancialFileId !== financialFile.id"
                        loader-height="2"
                        @click="setFinancialFile(financialFile.id);"
                    >
                        <v-card-text class="text-center px-3 py-1">
                            <v-img
                                width="120"
                                :height="$vuetify.breakpoint.lgAndUp && filteredFinancialFiles.length > 15 ? 80 : 110"
                                :src="
                                    financialFile.company_logo_file
                                        ? getFinancialFileLogoPath(financialFile.company_logo_file)
                                        : require('../../assets/placeholder-image.png')
                                "
                                contain
                                lazy-src="../../assets/placeholder-image.png"
                                transition="scale-transition"
                                class="mx-auto mb-1"
                            />
                            <p class="mb-0">
                                <span class="d-flex justify-center align-center primary--text text-body-1">
                                    <v-icon
                                        v-if="financialFile.is_locked && !financialFile.rotated_financial_file_id"
                                        size="20"
                                        color="grey darken-2"
                                        class="me-1"
                                    >
                                        mdi-lock
                                    </v-icon>
                                    <v-icon
                                        v-if="financialFile.is_archived"
                                        size="20"
                                        color="info-color"
                                        class="me-1"
                                    >
                                        mdi-archive
                                    </v-icon>
                                    <tooltip
                                        :text="$t(
                                            'QuickFinancialFileSelector.revolved-to-$', {
                                                financialFile: getFinancialFileById(financialFile.rotated_financial_file_id)?.company_name
                                            }
                                        )"
                                        :disabled="!financialFile.rotated_financial_file_id"
                                        bottom
                                    >
                                        <v-icon
                                            v-if="financialFile.rotated_financial_file_id"
                                            size="20"
                                            color="alert-color"
                                        >
                                            mdi-rotate-3d-variant
                                        </v-icon>
                
                                        {{financialFile.company_name}}
                                    </tooltip>
                                </span>
                                <span class="text-body-2">{{financialFile.year_name}}</span>
                            </p>
                        </v-card-text>
                    </v-card>
                </v-flex>

                <v-flex v-if="!filteredFinancialFiles.length" class="mt-6 text-center grey--text text--darken-1">
                    <div class="text-h2 mb-6" dir="ltr">
                        ¯\_(ツ)_/¯
                    </div>
                    <p class="text-h6">
                        {{
                            $t('QuickFinancialFileSelector.no-$-financial-files', {
                                state: [
                                    $t('QuickFinancialFileSelector.active'),
                                    $t('QuickFinancialFileSelector.locked'),
                                    $t('QuickFinancialFileSelector.revolved'),
                                    $t('QuickFinancialFileSelector.archived'),
                                ][showOption].toLowerCase()
                            })
                        }}
                    </p>
                </v-flex>
            </v-layout>
        </v-layout>
    </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import EventBus from '../../event-bus'
import ls from '../../settings/localStorageExtension'
import Tooltip from '../Tooltip.vue'
export default {
	components: { Tooltip },
    name: 'QuickFinancialFileSelector',
    
    data() {
        return {
            loading: false,
            selectedFinancialFileId: null,
            showOption: 0,
            showLocked: false,
            showRevolved: false,
            showArchived: false,
        }
    },

    computed: {
        ...mapState({
            financialFiles: state => state.financialFiles.financial_files
        }),
        ...mapGetters({
            getFinancialFileById: 'financialFiles/getFinancialFileById'
        }),

        filteredFinancialFiles() {
            switch(this.showOption) {
                case 0: return this.financialFiles.filter(c => !c.is_locked && !c.rotated_financial_file_id && !c.is_archived);
                case 1: return this.financialFiles.filter(c => c.is_locked && !c.rotated_financial_file_id);
                case 2: return this.financialFiles.filter(c => c.rotated_financial_file_id);
                case 3: return this.financialFiles.filter(c => c.is_archived);
                default: return [];
            }
            // return [
            //     this.showLocked
            //         ? this.financialFiles.filter(c => c.is_locked && !c.rotated_financial_file_id)
            //         : [],
            //     this.showRevolved
            //         ? this.financialFiles.filter(c => c.rotated_financial_file_id)
            //         : [],
            //     this.financialFiles.filter(c => !c.is_locked && !c.rotated_financial_file_id),
            // ].flat().filter(c => this.showArchived ? true : !c.is_archived)
        }
    },

    methods: {
        setFinancialFile(id){
            if (!this.selectedFinancialFileId) {
                this.selectedFinancialFileId = id;
                ls.setLocalFinancialFileId(id);
                if (this.$route.query.isChange) {
                    this.$router.replace({ name: 'home' }).finally(() => {
                        this.$router.go(0);
                    })
                } else {
                    this.initData(id);
                }
            }
        },

        initData(financialFileId) {
			if (financialFileId) {
				EventBus.$emit('financial-file-id-changed');
				if (ls.getLocalUser().is_admin) {
                    // this.$store.dispatch("financialFiles/fetchFinancialFile", financialFileId);
                    this.$router.replace({ name: 'home' });
                }
				else {
                    this.$store.dispatch('permissions/fetchPermissionType', {
						user_id: ls.getLocalUser().id,
						financial_file_id: financialFileId
					})
					.then(financialFileData => {
						if (financialFileData) {
							this.$router.replace({ name: 'home' });
							// fetch user date and save user creation financial file permission in localStorage
							this.$store.dispatch('users/fetchUser', ls.getLocalUser().id)
							.then(() => {
								ls.setLocalUser(
									JSON.stringify({
										...ls.getLocalUser(),
										can_create_financial_file: financialFileData.can_create_financial_file,
										permission_type: financialFileData.permission_type
									})
								);
							})
						}
						else {
                            this.$store.dispatch('auth/logout').then(() => {
								this.$router.replace({ name: 'login' }).then(() => {
									this.$router.go(0); /** Force refresh to empty the store */
								});
							});
                        }
					});
                }
			}
		},

        getFinancialFileLogoPath(fileName) {
			return axios.defaults.baseURL.replace('/api/', '') + '/Content/FinancialFiles/Logos/' + fileName;
		},
    },

    created() {
        // clearing current financial file
        ls.setLocalFinancialFileId(null);

        // fetching financial files
        this.loading = true;
        this.$store.dispatch("financialFiles/fetchAll", {}).finally(() => {
            this.loading = false;
        })
    },

    meta: {
		title: 'Quick financial file selector',
		titleTemplate: '%s | Emerald',
	}
}
</script>

<style lang="scss" scoped>
.loader {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100px;
}
.financial-file-card {
    display: flex;
    justify-content: center;
    align-items: center;
    
    border: 1px solid #ccc;

    opacity: 0;
    transform: scale(0);

    animation: fade-in .75s cubic-bezier(.42,.57,.14,1.28) forwards;

    @keyframes fade-in {
        from {
            opacity: 0;
            transform: scale(0);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }

    &__body {
        transition: .3s all;
    }
    &:hover {
        z-index: 1;
    }
    &:hover &__body{
        transform: scale(1.1);
        // outline: 1px solid #ccc;
        box-shadow:
            0px 3px 1px -2px rgba(0, 0, 0, 0.2),
            0px 2px 2px 0px rgba(0, 0, 0, 0.14),
            0px 1px 5px 0px rgba(0, 0, 0, 0.12) !important;
    }
}
</style>